import { useAuth0 } from '@auth0/auth0-react';
import { Alert, Button, Grid, Message, Modal, Input, Tag } from "@arco-design/web-react";
import { IconBug, IconPlayCircleFill, IconPlus } from "@arco-design/web-react/icon";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { DeleteApi, FetchApi, PostApi, SaveApi } from "../../Api/AuthenticatedApi";
import { uuidOrNull } from "../../utils/validators";
import { confirmIfLive } from "../../utils/confirmIfLive";
import { formEqual } from "../../utils/formEqual";
import { useUnsavedGuard } from "../../utils/useUnsavedGuard";
import Content from "../../Layout/Content";


import AccordionEditors from '../../Components/AccordionEditors/AccordionEditors';
import SimpleBreadcrumbs from '../../Components/Breadcrumbs/SimpleBreadcrumbs';
import UseLoadContacts from '../../Contacts/UseLoadContacts';
import MailVariantCard from "../BroadcastViewer/MailVariantCard";
import InfiniteScrollContactTable from './ContactTable';
import BroadcastSettingsEditor from './Editors/BroadcastSettingsEditor';
import RecordNotFound from "../../Components/RecordNotFound";

const { Row, Col } = Grid;

// MUI -> Arco. makeStyles -> styled-components. Toolbar -> a styled flex bar;
// ButtonGroup -> Button.Group; Button outlined -> type="outline", startIcon ->
// icon. Icons: BugReport -> IconBug, PlayCircleFilledWhite -> IconPlayCircleFill,
// PlusOne -> IconPlus. Alert -> Arco Alert (type/content). Grid /12 -> Arco
// Grid /24.
const Toolbar = styled.div`
    display: flex;
    align-items: center;
    min-height: 64px;
    padding: 0 8px;
`;

const Grow = styled.div`
    flex-grow: 1;
`;

// const EDIT_BUTTON_TEXT = "Edit"
// const CANCEL_BUTTON_TEXT = "Cancel"
// const DELETE_BUTTON_TEXT = "Delete"


// The broadcast settings values as loaded, for dirty-tracking. Mirrors the
// coercions in setFromRecord so a freshly-loaded form reads as clean.
function broadcastValues(r) {
    r = r || {};
    return {
        name: "" + (r.name != null ? r.name : ""),
        type: "" + (r.type != null ? r.type : ""),
        description: "" + (r.description != null ? r.description : ""),
        senderEmailId: "" + (r.sender_email_id != null ? r.sender_email_id : ""),
        contactListId: r.contact_list_id || "",
        segmentId: r.segment_id || "",
        audienceType: r.segment_id ? "segment" : "list",
    };
}

export default function EditBroadcast() {
    const { broadcastId } = useParams();
    const [record, setRecord] = useState({});
    const [, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);

    // const [editOrCancelText, setEditOrCancelText] = useState(EDIT_BUTTON_TEXT);

    const [broadcastName, setBroadcastName] = useState("");
    const [senderEmailId, setSenderEmailId] = useState("");
    const [confirmedSenderEmails, setConfirmedSenderEmails] = useState([]);
    const [broadcastDescription, setBroadcastDescription] = useState("");
    const [broadcastType, setBroadcastType] = useState("");
    const [mailVariants, setMailVariants] = useState([]);

    const [testModalVisible, setTestModalVisible] = useState(false);
    const [testEmail, setTestEmail] = useState("");

    const startBroadcast = () => {
        Modal.confirm({
            title: "Send this broadcast?",
            content: totalCount
                ? `This sends to ${totalCount.toLocaleString()} recipient${totalCount === 1 ? "" : "s"} and can't be undone.`
                : "This sends the broadcast now and can't be undone.",
            okText: "Send now",
            okButtonProps: { status: "warning" },
            onOk: () => {
                PostApi(getAccessTokenSilently, "/api/broadcasts/" + broadcastId + "/send",
                    (data) => { setFromRecord(data); Message.success("Broadcast is sending"); },
                    () => history("/login"),
                    // Validation failures (unverified sender, empty audience, no active
                    // variant, wrong status) come back as 400 with a fix-it message.
                    (err) => Message.error((err && err.message) || "Failed to send broadcast"),
                    {});
            },
        });
    }

    const testBroadcast = () => setTestModalVisible(true);

    const sendTest = () => {
        const email = testEmail.trim();
        if (!email) return;
        PostApi(getAccessTokenSilently, "/api/broadcasts/" + broadcastId + "/send-test",
            () => { setTestModalVisible(false); setTestEmail(""); Message.success("Test sent to " + email); },
            () => history("/login"),
            (err) => Message.error((err && err.message) || "Failed to send test"),
            { email });
    }



    const [allContactListItems, setAllContactListItems] = useState([]);
    const [allSegmentItems, setAllSegmentItems] = useState([]);
    const [segmentId, setSegmentId] = useState("");
    // Audience type: "list" (a contact list) or "segment" (a saved segment).
    const [audienceType, setAudienceType] = useState("list");
    // const [selectedSubscriberTotalCount, setSelectedSubscriberTotalCount] = useState();
    const [, setSelectAllLocal] = useState(false);
    const [order] = useState("asc");
    const [orderBy] = useState("email");

    const handleSave = (event) => {
        // The audience is a segment OR a list — never both. Clear the other side
        // so switching audiences doesn't leave a stale target.
        const targetingSegment = audienceType === "segment" && segmentId;
        saveBroadcast({
            name: broadcastName,
            type: broadcastType,
            description: broadcastDescription,
            contact_list_id: targetingSegment ? null : uuidOrNull(contactListFilterId),
            segment_id: targetingSegment ? uuidOrNull(segmentId) : null,
            sender_email_id: uuidOrNull(senderEmailId),
        });
    }

    const handleSegmentIdChange = (value) => {
        setSegmentId(value || "");
    }

    const handleDelete = (event) => {
        (async () => {
            let url = "/api/broadcasts/" + broadcastId
            DeleteApi(getAccessTokenSilently, url, (data) => {
                setLoading(false);
                // setRecord(data);
                // setFromRecord(data)
                Message.success("Broadcast deleted");
                history("/broadcasts/")
            }, (response) => {
                setLoading(false);
                history("/login");
            }, (response) => {
                setLoading(false);
                Message.error("Failed to delete broadcast");
            })
        })();
    }

    const setFromRecord = (r) => {
        setRecord(r);

        setBroadcastName("" + r.name);
        setBroadcastType("" + r.type);
        setSenderEmailId("" + r.sender_email_id);

        if (contactListFilterId !== r.contact_list_id) {
            setContactListFilterId(r.contact_list_id);
            setShouldUpdateDataFromServer([{ operation: "refresh" }]);
        }

        if (r.segment_id) {
            setSegmentId(r.segment_id);
            setAudienceType("segment");
        } else {
            setAudienceType("list");
        }

        // setThankYouUrl("" + r.form_thank_you_url)
        setBroadcastDescription(r.description)
    }

    const handleContactListIdChange = (event) => {
        setContactListFilterId(event.target.value);
        setShouldUpdateDataFromServer([{ operation: "refresh" }]);
    }

    const { getAccessTokenSilently } = useAuth0();
    let history = useNavigate();

    useEffect(() => {
        // setInitializing(true);
        FetchApi(getAccessTokenSilently,
            "/api/contact-lists/",
            (data) => {
                setAllContactListItems(data);
                // setInitializing(false);
                // setMailLis   tItems(data);
            },
            (response) => {
                // setInitializing(false);
                history("/login");
            }, (response) => {
                // setInitializing(false);
                Message.error("Failed to load data");
            })
    }, [history, getAccessTokenSilently])

    useEffect(() => {
        FetchApi(getAccessTokenSilently,
            "/api/segments/",
            (data) => setAllSegmentItems(data || []),
            () => history("/login"),
            () => {})
    }, [history, getAccessTokenSilently])


    useEffect(() => {
        (async () => {
            let url = "/api/broadcasts/" + broadcastId
            FetchApi(getAccessTokenSilently, url, (data) => {
                setLoading(false);
                // setRecord(data);
                setFromRecord(data)
            }, (response) => {
                setLoading(false);
                history("/login");
            }, (response) => {
                setLoading(false);
                setLoadFailed(true);
            })
        })();
        // broadcastId change remounts this route; setFromRecord is an unstable
        // inline closure over contactListFilterId, so excluding it avoids a
        // refetch loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setLoading, history, getAccessTokenSilently])

    useEffect(() => {
        (async () => {
            let url = "/api/settings/confirmed-sender-emails/"
            FetchApi(getAccessTokenSilently, url, (data) => {
                setLoading(false);
                // setRecord(data);
                setConfirmedSenderEmails(data)
            }, (response) => {
                setLoading(false);
                history("/login");
            }, (response) => {
                setLoading(false);
                Message.error("Failed to load data");
            })
        })();
    }, [setLoading, history, getAccessTokenSilently])

    const loadMailVariants = useCallback(() => {
        let url = "/api/broadcasts/" + broadcastId + "/mailvariants/"
        FetchApi(getAccessTokenSilently, url, (data) => {
            setLoading(false);
            // setRecord(data);
            setMailVariants(data)
            // setFromRecord(data)
        }, (response) => {
            setLoading(false);
            history("/login");
        }, (response) => {
            setLoading(false);
            Message.error("Failed to load data");
        })
    }, [broadcastId, setLoading, history, getAccessTokenSilently])

    useEffect(() => {
        loadMailVariants();
    }, [loadMailVariants])

    const { totalCount, contactListFilterId, setContactListFilterId, setShouldUpdateDataFromServer,
        loadItemsLoading, contactItems, hasNextPage, error: loadContactsError, loadMore } = UseLoadContacts();

    const [selected, setSelected] = useState([]);

    // Always-editable settings: dirty-tracking + a live-guard on save. A broadcast
    // that's scheduled/sending/sent is "live", so saving it confirms first.
    const settingsBaseline = broadcastValues(record);
    const settingsCurrent = {
        name: "" + broadcastName,
        type: "" + broadcastType,
        description: "" + (broadcastDescription != null ? broadcastDescription : ""),
        senderEmailId: "" + senderEmailId,
        contactListId: contactListFilterId || "",
        segmentId: segmentId || "",
        audienceType,
    };
    const settingsDirty = !formEqual(settingsCurrent, settingsBaseline);
    useUnsavedGuard(settingsDirty);

    const broadcastStatus = record && record.status;
    const broadcastIsLive =
        broadcastStatus === "sending" || broadcastStatus === "sent" || broadcastStatus === "scheduled";
    const guardedSaveSettings = async () => {
        const ok = await confirmIfLive({
            live: broadcastIsLive,
            title: "This broadcast is live",
            content: `“${record.name || "This broadcast"}” is ${broadcastStatus}. Saving applies your changes to it now.`,
        });
        if (!ok) return;
        handleSave();
    };
    const discardSettings = () => setFromRecord(record);




    // if (loading) {
    //     return (<Content>
    //         <CircularProgress />
    //     </Content>);
    // }

    function saveBroadcast(data) {
        (async () => {
            let url = "/api/broadcasts/" + broadcastId
            SaveApi(getAccessTokenSilently, url, (data) => {
                setLoading(false);
                setRecord(data);
                Message.success("Broadcast saved");
            }, (response) => {
                setLoading(false);
                history("/login");
            }, (response) => {
                setLoading(false);
                Message.error("Failed to save broadcast");
            }, data);
        })();
    }


    const emailRecipientsComponent = (
        <Row>
            <Col span={24}>

            </Col>
            <Col span={24}>
                <InfiniteScrollContactTable
                    contactItems={contactItems}
                    selected={selected}
                    setSelected={setSelected}
                    setSelectAllLocal={setSelectAllLocal}
                    order={order}
                    orderBy={orderBy}
                    contactListFilterId={contactListFilterId}
                    hasNextPage={hasNextPage}
                    loadItemsLoading={loadItemsLoading}
                    totalCount={totalCount}
                    loadMore={loadMore}
                    loadContactsError={loadContactsError}
                    handleContactRowClick={(event) => { }}
                />
            </Col>
        </Row>
    );

    const emailVariationsComponent = (
        <>
            <Row>
                <Col span={24}>
                    <Alert style={{ marginBottom: "10px" }} type="info" content={"You can A/B test (email variations) by adding/cloning."} />
                </Col>
                <Col span={24}>
                    <Row gutter={16} justify="start">
                        {
                            mailVariants && mailVariants.map((item, index) => {
                                return (
                                    <Col key={item.id} flex="none">
                                        <MailVariantCard item={item} index={index} editable={true} broadcastId={broadcastId} onChanged={loadMailVariants} />
                                    </Col>
                                )
                            })
                        }

                    </Row>

                </Col>
                <Col span={24}>
                    &nbsp;
                </Col>
                <Col span={24}>
                    {/* <ButtonGroup color="primary" aria-label="outlined primary button group"> */}
                    <Button type="outline" onClick={() => { history(`/broadcasts/` + broadcastId + `/email/new`) }}
                        icon={<IconPlus />}>
                        Add New Email
                    </Button>
                    {/* <Button variant="outlined" color="primary" disabled={!broadcastStarted} onClick={startBroadcast}>Start Broadcast</Button> */}
                    {/* </ButtonGroup> */}
                </Col>
            </Row>
        </>

    );

    if (loadFailed && !record?.id) {
        return <RecordNotFound what="broadcast" backTo="/broadcasts" backLabel="Back to Broadcasts" />;
    }

    return (<Content>
        {/* <div
            style={{
                height: "200px",
                backgroundPosition: "center",
                backgroundSize: "cover",
                filter: "contrast(75%)",
                backgroundImage: "url(/img/wallpaper.jpeg)",
            }}
        /> */}

        <div>
            <SimpleBreadcrumbs
                elements={
                    [
                        { name: 'Broadcasts', path: '/broadcasts' },
                        { name: record.name, path: `/broadcasts/${broadcastId}/view` },
                        { name: "Edit" },
                    ]
                }

            />
        </div>

        {/* <Snackbar open={snackOpen} autoHideDuration={2000} onClose={snackClose}>
            <Alert onClose={snackClose} severity="success">
                {snackOpen}
            </Alert>
        </Snackbar> */}

        <Toolbar>
            {record.status && (
                <Tag color={record.status === "sent" ? "green" : record.status === "sending" ? "arcoblue" : record.status === "scheduled" ? "orange" : "gray"}>
                    {record.status}
                </Tag>
            )}
            <Grow />
            <Button.Group>
                <Button type="outline" icon={(<IconBug />)} onClick={testBroadcast}>Test</Button>
                <Button
                    type="outline"
                    icon={(<IconPlayCircleFill />)}
                    disabled={record.status === "sending" || record.status === "sent"}
                    onClick={startBroadcast}
                >
                    Start
                </Button>
            </Button.Group>
        </Toolbar>


        <AccordionEditors
            items={[
                {
                    title: "Configure Broadcast",
                    component: <BroadcastSettingsEditor
                        broadcastName={broadcastName} setBroadcastName={setBroadcastName}
                        broadcastType={broadcastType} setBroadcastType={setBroadcastType}
                        broadcastDescription={broadcastDescription} setBroadcastDescription={setBroadcastDescription}
                        senderEmailId={senderEmailId} setSenderEmailId={setSenderEmailId}
                        confirmedSenderEmails={confirmedSenderEmails}
                        allContactListItems={allContactListItems}
                        setAllContactListItems={setAllContactListItems}
                        contactListFilterId={contactListFilterId}
                        handleContactListIdChange={handleContactListIdChange}
                        allSegmentItems={allSegmentItems}
                        segmentId={segmentId}
                        handleSegmentIdChange={handleSegmentIdChange}
                        audienceType={audienceType}
                        setAudienceType={setAudienceType}
                        handleSave={guardedSaveSettings}
                        isDirty={settingsDirty}
                        onDiscard={discardSettings}
                        handleDelete={handleDelete}
                        record={record}
                        setFromRecord={setFromRecord}
                    />,
                },
                {
                    title: totalCount === 1 ? 'Sending to 1 recipient' : totalCount === 0
                        ? "No Recipients, Choose a Contact List" : `Sending to ${totalCount} Recipients`,
                    component: emailRecipientsComponent,
                },
                {
                    title: (!mailVariants || mailVariants.length === 0) ? "Add Email" : mailVariants.length === 1
                        ? "1 Email (add another for A/B testing)" : `A/B Testing ${mailVariants.length} Emails`,
                    component: emailVariationsComponent,
                }

            ]}

        />


        <Modal
            title="Send a test email"
            visible={testModalVisible}
            onOk={sendTest}
            onCancel={() => setTestModalVisible(false)}
            okText="Send test"
            simple
        >
            <p style={{ color: "var(--color-text-3)", marginTop: 0 }}>Sends one preview email using this broadcast's active variant.</p>
            <Input placeholder="you@example.com" value={testEmail} onChange={setTestEmail} onPressEnter={sendTest} />
        </Modal>

    </Content>);
}
